import React, { useState } from 'react';
import HeaderLogged from './components/header_logged';
import Container from './components/container';
import SelectBox from './components/SelectBox';
import api from './api/client';
import Notification from './components/Notification';

const emptyForm = {
    rawMaterialID: '',
    bagNumbers: '',
    materialName: '',
    containerID: 1,
    lotSupplier: '',
    expirationDate: '',
    currentWeightLb: '',
};

const ScannersInvFert = () => {
    const [form1State, setForm1State] = useState({ ...emptyForm });
    const [form2State, setForm2State] = useState({ ...emptyForm });

    const [notification, setNotification] = useState({ message: '', type: '' });
    const [selectedVariant, setSelectedVariant] = useState('scanner');
    const [submitting, setSubmitting] = useState(false);

    const handleVariantChange = (e) => {
        setSelectedVariant(e.target.value);
        if (e.target.value === 'scanner') {
            setForm2State({ ...emptyForm });
        } else {
            setForm1State({ ...emptyForm });
        }
    };

    const handleForm1Change = (e) => {
        const { name, value } = e.target;
        setForm1State((prev) => ({ ...prev, [name]: value }));
    };

    const handleForm2Change = (e) => {
        const { name, value } = e.target;
        setForm2State((prev) => ({ ...prev, [name]: value }));
    };

    const fetchMaterialName = async (rawMaterialID) => {
        try {
            const response = await api.get(`/api/fertrawmaterial/${rawMaterialID}`);
            return response.data.materialName || '';
        } catch (error) {
            console.error('Error fetching material name:', error);
            return '';
        }
    };

    const handleForm1RawMaterialIDChange = async (e) => {
        const rawMaterialID = e.target.value;
        setForm1State((prev) => ({ ...prev, rawMaterialID }));

        if (rawMaterialID) {
            const materialName = await fetchMaterialName(rawMaterialID);
            setForm1State((prev) => ({ ...prev, materialName }));
        } else {
            setForm1State((prev) => ({ ...prev, materialName: '' }));
        }
    };

    const parseBagNumbers = (text) =>
        text.split('\n').map(s => s.trim()).filter(Boolean);

    const submitBags = async (state) => {
        const { rawMaterialID, bagNumbers, containerID, lotSupplier, expirationDate, currentWeightLb, materialName } = state;
        const numbers = parseBagNumbers(bagNumbers);

        if (!rawMaterialID) {
            setNotification({ message: 'Please select a fertilizer material.', type: 'error' });
            return false;
        }
        if (numbers.length === 0) {
            setNotification({ message: 'Please enter at least one bag number.', type: 'error' });
            return false;
        }
        if (!currentWeightLb) {
            setNotification({ message: 'Please enter the weight.', type: 'error' });
            return false;
        }

        const errors = [];
        let saved = 0;

        for (const bagNumber of numbers) {
            try {
                const checkResp = await api.get(`/api/fertbagstock/${rawMaterialID}/${bagNumber}`);
                if (checkResp.data && checkResp.data.exists) {
                    await api.put(`/api/fertbagstock/${rawMaterialID}/${bagNumber}`, {
                        containerID, lotSupplier, expirationDate, currentWeightLb, materialName,
                    });
                } else {
                    await api.post(`/api/fertbagstock`, {
                        rawMaterialID, bagNumber, containerID, lotSupplier, expirationDate, currentWeightLb, materialName,
                    });
                }
                saved++;
            } catch (err) {
                console.error(`Error saving bag ${bagNumber}:`, err);
                errors.push(bagNumber);
            }
        }

        if (errors.length === 0) {
            setNotification({ message: `${saved} bag(s) saved successfully!`, type: 'success' });
        } else {
            setNotification({ message: `Saved ${saved}, failed: ${errors.join(', ')}`, type: 'error' });
        }
        return errors.length === 0;
    };

    const handleForm1Submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const ok = await submitBags(form1State);
        if (ok) setForm1State((prev) => ({ ...prev, bagNumbers: '', currentWeightLb: '' }));
        setSubmitting(false);
    };

    const handleForm2MaterialChange = async (value) => {
        setForm2State((prev) => ({ ...prev, rawMaterialID: value }));
        if (value) {
            const materialName = await fetchMaterialName(value);
            setForm2State((prev) => ({ ...prev, materialName }));
        } else {
            setForm2State((prev) => ({ ...prev, materialName: '' }));
        }
    };

    const handleForm2Submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const ok = await submitBags(form2State);
        if (ok) setForm2State((prev) => ({ ...prev, bagNumbers: '', currentWeightLb: '' }));
        setSubmitting(false);
    };

    const closeNotification = () => setNotification({ message: '', type: '' });

    const bagCountLabel = (bagNumbers) => {
        const n = parseBagNumbers(bagNumbers).length;
        return n > 0 ? `${n} bag${n > 1 ? 's' : ''} entered` : null;
    };

    return (
        <div className="scanners-inv-seeds-page-bg">
            <Notification message={notification.message} type={notification.type} onClose={closeNotification} />
            <HeaderLogged />
            <Container className="scanners-inv-seeds-page">
                <div className="seedsinv-variants-inner">
                    <label className="seedsinv-fromvariant">
                        <input type="radio" value="scanner" name="fertinvinputvariant" defaultChecked onChange={handleVariantChange} />
                        <span className="custom-radio"></span>
                        Using Scanner
                    </label>
                    <label className="seedsinv-fromvariant">
                        <input type="radio" value="fertname" name="fertinvinputvariant" onChange={handleVariantChange} />
                        <span className="custom-radio"></span>
                        Using Fertilizer Name
                    </label>
                </div>

                {/* Form 1 — Using Scanner */}
                <div className={`scannerform-inner ${selectedVariant === 'scanner' ? 'active-formvar' : 'hidden-formvar'}`}>
                    <form className="scannerform" onSubmit={handleForm1Submit}>
                        <h2 className="popup__title">Fertilizer Bag Inventory</h2>
                        <h3 className="popup__subtitle">Please use the scanner to input actual data</h3>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Fertilizer ID</label>
                            <input
                                className='scannerform__input'
                                name="rawMaterialID"
                                value={form1State.rawMaterialID}
                                onChange={handleForm1RawMaterialIDChange}
                                placeholder='Please scan the QR-code for fertilizer'
                            />
                        </div>
                        {form1State.materialName && (
                            <div className="scannerform-inputgroup dynamic-label visible-label" style={{ maxWidth: '250px' }}>
                                <label className='scannerform-label'>
                                    Material — <span className='scannerform-dynlabel'>{form1State.materialName}</span>
                                </label>
                            </div>
                        )}
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>
                                Bag Numbers
                                {bagCountLabel(form1State.bagNumbers) && (
                                    <span className='scannerform-dynlabel' style={{ marginLeft: 8 }}>
                                        ({bagCountLabel(form1State.bagNumbers)})
                                    </span>
                                )}
                            </label>
                            <textarea
                                className='scannerform__input'
                                name="bagNumbers"
                                value={form1State.bagNumbers}
                                onChange={handleForm1Change}
                                placeholder={'Scan or type each bag number on a new line\nExample:\nA0001\nA0002\nA0003'}
                                rows={5}
                                style={{ resize: 'vertical', fontFamily: 'monospace' }}
                            />
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Container Name</label>
                            <SelectBox
                                tableName="containers"
                                displayField="containerName"
                                updateField="containerID"
                                selectedValue={form1State.containerID}
                                onChange={(value) => setForm1State({ ...form1State, containerID: value })}
                            />
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Supplier Lot</label>
                            <input
                                className='scannerform__input'
                                name="lotSupplier"
                                value={form1State.lotSupplier}
                                onChange={handleForm1Change}
                                placeholder='Supplier lot number'
                                maxLength={50}
                            />
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Expiration Date</label>
                            <input
                                className='scannerform__input'
                                type="date"
                                name="expirationDate"
                                value={form1State.expirationDate}
                                onChange={handleForm1Change}
                            />
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Current Weight (lb)</label>
                            <input
                                className='scannerform__input'
                                name="currentWeightLb"
                                value={form1State.currentWeightLb}
                                onChange={handleForm1Change}
                                placeholder='Example: 50.5'
                            />
                        </div>
                        <button type="submit" className="btn btn__green mt-15" disabled={submitting}>
                            {submitting ? 'Saving...' : 'Add Records'}
                        </button>
                    </form>
                </div>

                {/* Form 2 — Using Fertilizer Name */}
                <div className={`scannerform-inner ${selectedVariant === 'fertname' ? 'active-formvar' : 'hidden-formvar'}`}>
                    <form className="scannerform" onSubmit={handleForm2Submit}>
                        <h2 className="popup__title">Fertilizer Bag Inventory</h2>
                        <h3 className="popup__subtitle">Please use the scanner to input actual data</h3>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Fertilizer</label>
                            <SelectBox
                                tableName="fert_raw_materials"
                                displayField="materialName"
                                updateField="id"
                                selectedValue={form2State.rawMaterialID}
                                onChange={handleForm2MaterialChange}
                            />
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>
                                Bag Numbers
                                {bagCountLabel(form2State.bagNumbers) && (
                                    <span className='scannerform-dynlabel' style={{ marginLeft: 8 }}>
                                        ({bagCountLabel(form2State.bagNumbers)})
                                    </span>
                                )}
                            </label>
                            <textarea
                                className='scannerform__input'
                                name="bagNumbers"
                                value={form2State.bagNumbers}
                                onChange={handleForm2Change}
                                placeholder={'Type each bag number on a new line\nExample:\nA0001\nA0002\nA0003'}
                                rows={5}
                                style={{ resize: 'vertical', fontFamily: 'monospace' }}
                            />
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Container Name</label>
                            <SelectBox
                                tableName="containers"
                                displayField="containerName"
                                updateField="containerID"
                                selectedValue={form2State.containerID}
                                onChange={(value) => setForm2State({ ...form2State, containerID: value })}
                            />
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Supplier Lot</label>
                            <input
                                className='scannerform__input'
                                name="lotSupplier"
                                value={form2State.lotSupplier}
                                onChange={handleForm2Change}
                                placeholder='Supplier lot number'
                                maxLength={50}
                            />
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Expiration Date</label>
                            <input
                                className='scannerform__input'
                                type="date"
                                name="expirationDate"
                                value={form2State.expirationDate}
                                onChange={handleForm2Change}
                            />
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Current Weight (lb)</label>
                            <input
                                className='scannerform__input'
                                name="currentWeightLb"
                                value={form2State.currentWeightLb}
                                onChange={handleForm2Change}
                                placeholder='Example: 50.5'
                            />
                        </div>
                        <button type="submit" className="btn btn__green mt-15" disabled={submitting}>
                            {submitting ? 'Saving...' : 'Add Records'}
                        </button>
                    </form>
                </div>
            </Container>
        </div>
    );
};

export default ScannersInvFert;
